// ABOUTME: Modal to add a return-cost tariff (profile + countries + webshops + cost in cents).
// ABOUTME: Hands the built tariff back to the parent via onAdd; the parent persists the tariff array.

import { Box, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import { InputDropdown } from "../../theme/dropdowns/InputDropdown";
import NumberInput from "../../theme/inputs/NumberInput";
import CountryPoolSelector from "../CountryPoolSelector";
import ReturnProfileSelector from "../ReturnProfileSelector";

const MAddReturnCostProfile = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [shipperProfileId, setShipperProfileId] = useState(-1);
    const [allowedCountries, setAllowedCountries] = useState([]);
    const [webshopCredentials, setWebshopCredentials] = useState([]);
    const [costInCents, setCostInCents] = useState(0);

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    // Countries are constrained to the selected profile's own allowed countries.
    const countryPool = useMemo(
        () => (props.returnProfiles ?? []).find((p) => p.id === shipperProfileId)?.returnOptions?.countries ?? [],
        [props.returnProfiles, shipperProfileId]
    );

    // Manually created orders have no webshop credentials, so they are selectable
    // as the sentinel id -1 that the backend matches a null webshopCredentialsId to.
    const webshopOptions = useMemo(() => ([
        { id: -1, label: t("self_created") },
        ...(props.webshops ?? []).map((webshop) => ({
            id: webshop.id,
            label: `${webshop.webshop_type} - ${webshop.description}`,
        })),
    ]), [props.webshops, t]);

    const resetForm = () => {
        setShipperProfileId(-1);
        setAllowedCountries([]);
        setWebshopCredentials([]);
        setCostInCents(0);
    };

    const handleClose = () => {
        resetForm();
        props.handleClose();
    };

    // Changing the profile changes the allowed country pool, so reset the country selection.
    const handleProfileChange = (value) => {
        setShipperProfileId(value ?? -1);
        setAllowedCountries([]);
    };

    const isValid =
        shipperProfileId !== -1 &&
        allowedCountries.length > 0 &&
        webshopCredentials.length > 0 &&
        costInCents !== "" &&
        !isNaN(parseInt(costInCents, 10));

    const handleAdd = () => {
        if (!isValid) {
            return;
        }
        props.onAdd({
            shipperProfileId,
            allowedCountries,
            webshopCredentials,
            costInCents: parseInt(costInCents, 10) || 0,
        });
        resetForm();
    };

    return (
        <SHModal open={props.open} onClose={handleClose}>
            <Box sx={{ textAlign: "center", paddingY: 4, paddingX: 5, display: "flex", flexDirection: "column", gap: 1 }}>
                <Subheading>{t("new_return_cost_profile")}</Subheading>
            </Box>

            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                    <Box sx={{ width: 140, flexShrink: 0 }}>
                        <Text>{t("return_profile")}</Text>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <ReturnProfileSelector
                            returnProfiles={props.returnProfiles ?? []}
                            selected={shipperProfileId}
                            onChange={handleProfileChange}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                    <Box sx={{ width: 140, flexShrink: 0 }}>
                        <Text>{t("countries")}</Text>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <CountryPoolSelector
                            countryPool={countryPool}
                            selected={allowedCountries}
                            onChange={setAllowedCountries}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                    <Box sx={{ width: 140, flexShrink: 0 }}>
                        <Text>{t("webshops")}</Text>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <InputDropdown
                            multiple
                            allowSelectAll
                            height={40}
                            valueKey="id"
                            displayName="label"
                            options={webshopOptions}
                            selected={webshopCredentials}
                            onChange={setWebshopCredentials}
                            noSelection={t("choose")}
                            searchOptions={{ enabled: true, placeholder: t("search") }}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                    <Box sx={{ width: 140, flexShrink: 0 }}>
                        <Text>{t("return_portal_return_cost_title")}</Text>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box sx={{ width: 160 }}>
                            <NumberInput
                                value={costInCents}
                                change={setCostInCents}
                                onlyPositive
                                min={0}
                                changeBy={1}
                                btnSize={"24"}
                                height={40}
                            />
                        </Box>
                        <Text noBreak bold>{t("in_cent")}</Text>
                    </Box>
                </Box>
            </Box>

            <Box
                component="footer"
                sx={{
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingRight: 3,
                    zIndex: 1058,
                    position: "relative",
                    paddingTop: 2,
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    "@media screen and (max-width: 47.9375em)": { paddingRight: 0 },
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon />}>
                    {t("cancel")}
                </Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton block className={"group-btn"} color={"green"} variant="contained" disabled={!isValid} onClick={handleAdd}>
                        {t("add")}
                    </Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    );
};

export default MAddReturnCostProfile;
